import type { ISettingsRepository, SettingsKey } from '@app/domain/ports';

const STORAGE_PREFIX = 'fin';

/**
 * Implementation of ISettingsRepository using localStorage.
 * Provides plain storage without encryption logic.
 *
 * Note: This repository does not decide which keys need encryption.
 * Encryption should be handled by a decorator in the application layer.
 */
export class SettingsRepository implements ISettingsRepository {
  // Synchronous methods
  get<T>(key: SettingsKey): T | undefined;
  get<T>(key: SettingsKey, defaultValue: T): T;
  get<T>(key: SettingsKey, defaultValue?: T): T | undefined {
    const raw = localStorage.getItem(this.prefixKey(key));
    if (raw === null || raw === undefined) {
      return defaultValue;
    }

    try {
      return JSON.parse(raw, this.dateReviver) as T;
    } catch (error) {
      console.warn('Failed to parse JSON from localStorage', error);
      return defaultValue;
    }
  }

  set(key: SettingsKey, value: unknown): void {
    const serialized = JSON.stringify(value);
    localStorage.setItem(this.prefixKey(key), serialized);
  }

  remove(key: SettingsKey): void {
    localStorage.removeItem(this.prefixKey(key));
  }

  // Asynchronous methods - wrap sync methods
  async getAsync<T>(key: SettingsKey): Promise<T | undefined>;
  async getAsync<T>(key: SettingsKey, defaultValue: T): Promise<T>;
  async getAsync<T>(key: SettingsKey, defaultValue?: T): Promise<T | undefined> {
    return Promise.resolve(this.get(key, defaultValue));
  }

  setAsync(key: SettingsKey, value: unknown): Promise<void> {
    this.set(key, value);
    return Promise.resolve();
  }

  removeAsync(key: SettingsKey): Promise<void> {
    this.remove(key);
    return Promise.resolve();
  }

  private prefixKey(key: SettingsKey): string {
    return `${STORAGE_PREFIX}:${key}`;
  }

  /**
   * JSON reviver that converts ISO date strings back to Date objects.
   * Matches strings like "2025-12-04T10:30:00.000Z"
   */
  private dateReviver(_key: string, value: unknown): unknown {
    if (typeof value === 'string') {
      // ISO 8601 date pattern
      const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{3})?Z?$/;
      if (isoDateRegex.test(value)) {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date;
        }
      }
    }
    return value;
  }
}
